import { createRouter, createWebHistory } from '@ionic/vue-router'
import { RouteRecordRaw } from 'vue-router'
import TabsView from '../views/TabsView.vue'
import { useAuthStore } from '@/stores/auth'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { onboardingRoutes } from './onboarding'
import profileRoutes from './ProfileRouters'
import categoryRoutes from './CategoryRouters'
import { usePostHog } from '@/composables/usePostHog'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/auth/login',
  },
  {
    path: '/svg-test',
    component: () => import('@/views/SvgTestView.vue'),
  },
  {
    path: '/pricing',
    meta: { requiresAuth: true },
    component: () =>
      Capacitor.isNativePlatform()
        ? import('@/views/auth/LoginPage.vue')
        : import('@/views/PricingPage.vue'),
  },
  {
    path: '/checkoutSuccess',
    meta: { requiresAuth: true },
    component: () => import('@/views/CheckoutSuccessPage.vue'),
  },

  {
    path: '/auth/login',
    component: () => import('@/views/auth/LoginPage.vue'),
  },
  {
    path: '/subscriptionExpired',
    component: () => import('@/views/InactiveSubscriptionPage.vue'),
  },
  {
    path: '/auth/signup',
    component: () => import('@/views/auth/SignupPage.vue'),
  },
  {
    path: '/auth/create-password',
    component: () => import('@/views/auth/CreatePasswordPage.vue'),
  },
  {
    path: '/auth/verify-email',
    component: () => import('@/views/auth/VerifyEmailPage.vue'),
  },
  {
    path: '/auth/forgot-password-email',
    component: () => import('@/views/auth/ForgotPasswordEmailInput.vue'),
  },
  {
    path: '/auth/forgot-password',
    component: () => import('@/views/auth/ForgotPasswordPage.vue'),
  },
  {
    path: '/auth/reset-password', // forgot password reset
    component: () => import('@/views/auth/ResetPasswordPage.vue'),
  },
  {
    path: '/auth/change-password',
    component: () => import('@/views/auth/ChangePasswordPage.vue'),
  },
  {
    path: '/auth/verification-complete',
    component: () => import('@/views/auth/VerificationCompletePage.vue'),
  },
  {
    path: '/onboarding/add-income',
    meta: { requiresAuth: true },
    component: () => import('@/views/onboarding/AddIncomePage.vue'),
  },
  {
    path: '/onboarding/add-expenses',
    meta: { requiresAuth: true },
    component: () => import('@/views/onboarding/AddExpensesPage.vue'),
  },
  {
    path: '/onboarding/add-accounts',
    meta: { requiresAuth: true },
    component: () => import('@/views/onboarding/AddAccountsPage.vue'),
  },
  {
    path: '/onboarding/welcome',
    meta: { requiresAuth: true },
    component: () => import('@/views/onboarding/WelcomePage.vue'),
  },
  {
    path: '/auth/signup-success',
    component: () => import('@/views/auth/SignupSuccessPage.vue'),
  },
  {
    path: '/onboarding/setup-complete',
    component: () => import('@/views/onboarding/SetupCompletePage.vue'),
  },
  {
    path: '/onboarding/start-trial',
    // meta: { requiresAuth: true },
    component: () => import('@/views/onboarding/StartTrialPage.vue'),
  },
  {
    path: '/manage-budget',
    meta: { requiresAuth: true },
    component: () => import('@/views/ManageBudgetPage.vue'),
  },
  {
    path: '/onboarding/trial-faq',
    // meta: { requiresAuth: true },
    component: () => import('@/views/onboarding/TrialFaqPage.vue'),
  },
  {
    path: '/onboarding/trial-success',
    name: 'TrialSuccess',
    component: () => import('@/views/onboarding/TrialSuccessPage.vue'),
  },
  {
    path: '/tabs',
    component: TabsView,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/tabs/balance',
      },
      {
        path: 'balance',
        component: () => import('@/views/BalancePage.vue'),
      },
      {
        path: 'category/:id',
        component: () => import('@/views/CategoryPage.vue'),
      },
      {
        path: 'transactions',
        component: () => import('@/views/TransactionsPage.vue'),
      },
      {
        path: 'accounts',
        component: () => import('@/views/AccountsPage.vue'),
      },
      {
        path: 'accounts/comparison',
        component: () => import('@/views/ComparisonPage.vue'),
      },
      {
        path: 'accounts/:itemId',
        component: () => import('@/views/AccountDetailPage.vue'),
      },
      {
        path: 'accounts/:itemId/:accountId/settings',
        component: () => import('@/views/AccountSettingsPage.vue'),
      },
    ],
  },
  {
    path: '/transactions/:transactionId/categories',
    meta: { requiresAuth: true },
    component: () => import('@/views/CategorySelectPage.vue'),
  },
  {
    path: '/add-transaction',
    meta: { requiresAuth: true },
    component: () => import('@/views/AddTransactionPage.vue'),
  },
  {
    path: '/add-transaction/categories',
    meta: { requiresAuth: true },
    component: () => import('@/views/CategorySelectPage.vue'),
  },
  {
    path: '/edit-transaction/:transactionId',
    meta: { requiresAuth: true },
    component: () => import('@/views/AddTransactionPage.vue'),
  },
  {
    path: '/edit-transaction/:transactionId/categories',
    meta: { requiresAuth: true },
    component: () => import('@/views/CategorySelectPage.vue'),
  },
  {
    path: '/transactions/:transactionId/details',
    meta: { requiresAuth: true },
    component: () => import('@/views/TransactionDetailsPage.vue'),
  },
  {
    path: '/transactions/bulk-categories',
    meta: { requiresAuth: true },
    component: () => import('@/views/CategorySelectPage.vue'),
  },
  {
    path: '/close-browser',
    component: () => import('@/views/CloseBrowserPage.vue'),
  },
  {
    path: '/plaid/redirect',
    component: () => import('@/views/PlaidRedirectPage.vue'),
  },
  ...profileRoutes,
]

const allRoutes = [...routes, ...onboardingRoutes, ...categoryRoutes]

const { posthog } = usePostHog()

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: allRoutes,
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (to.path == '/onboarding/add-accounts') {
    return next()
  }
  if (
    to.meta.requiresAuth &&
    !authStore.isLoggedIn &&
    !to.query.hasOwnProperty('session_id') &&
    !to.query.hasOwnProperty('token')
  ) {
    next({ path: '/auth/login', query: { redirect: to.fullPath } })
  } else if (to.meta.requiresAuth) {
    if (to.path == '/pricing') {
      if (authStore.hasActiveSubscription) {
        next('/onboarding/add-accounts')
      }
      if (Capacitor.isNativePlatform()) {
        await Browser.open({
          url: `${import.meta.env.VITE_APP_URL}/pricing?token=${
            authStore.token
          }&mobile=true`,
          presentationStyle: 'popover',
        })
        Browser.addListener('browserFinished', async () => {
          let retries = 3
          while (retries > 0) {
            await authStore.getUser()
            if (authStore.hasActiveSubscription) {
              authStore.onboardingComplete = true
              router.push('/onboarding/add-accounts')
              return
            }
            retries--
            await new Promise((resolve) => setTimeout(resolve, 5000))
          }
          router.push('/tabs/balance')
        })
        return
      }
      return next()
    }
    return next()
  } else {
    next()
  }
})

export default router
